import React from 'react';
import isEqual from 'react-fast-compare';
import {omit} from 'underscore';

import {SequenceSchemaType, TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import FieldTableRow from 'registry/field/FieldTableRow';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

type SchemaFieldWithOriginalField = SchemaField & {
  originalField: SchemaField;
};

export interface AddSchemaInterfaceModalFieldTableRowProps {
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  ownerUrlId: string;
  id: string;
  initialField: SchemaFieldWithOriginalField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
  storableSchemas?: Array<StorableSchema>;
  schemaInterfaces?: Array<SchemaInterface>;
  shouldShowIsRequired: boolean;
  submissionWarnings: Array<JSX.Element>;
  fileType?: TagSchemaSupportedFolderItemType | null;
  sequenceType?: SequenceSchemaType | null;
}

export const fieldsetControlledFieldAttributeToMessageMap: Map<
  keyof SchemaField,
  {text: string; bannerType: InlineBannerType} | null
> = new Map([
  [
    'isRequired',
    {
      text: 'Is required changed',
      bannerType: InlineBannerType.WARNING,
    },
  ],
  [
    'isMulti',
    {
      text: 'Is multi changed',
      bannerType: InlineBannerType.WARNING,
    },
  ],
  [
    'isParentLink',
    {
      text: 'Is parent link changed',
      bannerType: InlineBannerType.WARNING,
    },
  ],
  [
    'systemName',
    {
      text: 'System name changed',
      bannerType: InlineBannerType.WARNING,
    },
  ],
  [
    'displayName',
    {
      text: 'Display name changed',
      bannerType: InlineBannerType.WARNING,
    },
  ],
  ['name', null], // We prefer to rely on displayName for name changes
  [
    'fieldType',
    {
      text: 'Field definition mismatch',
      bannerType: InlineBannerType.ERROR,
    },
  ],
  [
    'isComputed',
    {
      text: 'Is computed changed',
      bannerType: InlineBannerType.ERROR,
    },
  ],
  [
    'requiredLink',
    {
      text: 'Entity link type mismatch',
      bannerType: InlineBannerType.ERROR,
    },
  ],
  [
    'schemaFieldSelectorId',
    {
      text: 'Schema field selector mismatch',
      bannerType: InlineBannerType.ERROR,
    },
  ],
]);

/** This component will diff two schema fields and show warnings / errors for what has changed. */
export default function AddSchemaInterfaceModalFieldTableRow(
  props: AddSchemaInterfaceModalFieldTableRowProps
): JSX.Element {
  /** The updated field is the schema interface field we are trying to match. */
  const updatedField = omit(props.initialField, 'originalField');
  /** The original field is the current field on the tag schema. */
  const originalField = props.initialField.originalField;
  const messages: Array<InlineBannerProps> = [];
  if (originalField != null) {
    fieldsetControlledFieldAttributeToMessageMap.forEach((message, key) => {
      if (message != null && !isEqual(updatedField[key], originalField[key])) {
        messages.push({
          text: message.text,
          bannerType: message.bannerType,
        });
      }
    });
  }
  return (
    <>
      <FieldTableRow
        availableFieldOptions={[]}
        {...props}
        initialField={updatedField}
        isUneditable={false}
        disableDrag={true}
        shouldShowWarehouseWarning={true}
        schemaId={null}
        disabled={true}
      />
      <InlineBannerStack messages={messages} />
    </>
  );
}
